import { useCallback, useEffect, useState } from 'react';

/**
 * Theme preference, remembered per browser.
 *
 * `system` defers to the operating system; the other two are explicit
 * overrides that outrank it.
 *
 * The storage key, the resolution rule and the theme colours below are
 * mirrored by the pre-paint script in `index.html`. That script has to run
 * before this module is even parsed - otherwise a user whose stored choice
 * disagrees with their OS gets a flash of the wrong theme - so it cannot
 * import from here. Change one, change the other.
 */
export type Theme = 'light' | 'dark' | 'system';
export type ResolvedTheme = 'light' | 'dark';

export const THEME_STORAGE_KEY = 'register.theme';

/**
 * What `<meta name="theme-color">` is set to for each theme. An installed app
 * paints its status bar with this, and the bar visually joins the masthead, so
 * these are the masthead's own colours: `--color-raised` in styles.css is the
 * third copy of each value.
 */
export const THEME_COLOR: Record<ResolvedTheme, string> = {
  light: '#ffffff',
  dark: '#161d25',
};

const CYCLE: readonly Theme[] = ['light', 'dark', 'system'];

function isTheme(value: unknown): value is Theme {
  return value === 'light' || value === 'dark' || value === 'system';
}

/** The next state of the cycling toggle: Light -> Dark -> System -> Light. */
export function nextTheme(theme: Theme): Theme {
  return CYCLE[(CYCLE.indexOf(theme) + 1) % CYCLE.length] as Theme;
}

/**
 * Resolve a preference into the theme actually shown.
 *
 * `system` follows `prefers-color-scheme`, falling back to dark when the
 * browser reports neither light nor dark. That last case is the `no-preference`
 * state, which browsers report only very rarely - so in practice this follows
 * the OS, and the dark fallback is a backstop rather than the common path.
 */
export function resolveTheme(theme: Theme): ResolvedTheme {
  if (theme !== 'system') return theme;
  if (window.matchMedia('(prefers-color-scheme: dark)').matches) return 'dark';
  if (window.matchMedia('(prefers-color-scheme: light)').matches) return 'light';
  return 'dark';
}

/**
 * The one place the DOM is written: the attribute the stylesheet keys off,
 * and the meta tag the browser chrome keys off. The meta is optional so a
 * page without it (tests, or a future embed) still themes.
 */
export function applyResolvedTheme(resolved: ResolvedTheme): void {
  document.documentElement.dataset.theme = resolved;
  document
    .querySelector('meta[name="theme-color"]')
    ?.setAttribute('content', THEME_COLOR[resolved]);
}

/** localStorage throws outright in some privacy modes. A colour preference is
 *  never worth taking the register down for. */
function readStoredTheme(): Theme {
  try {
    const stored = localStorage.getItem(THEME_STORAGE_KEY);
    return isTheme(stored) ? stored : 'system';
  } catch {
    return 'system';
  }
}

function writeStoredTheme(theme: Theme): void {
  try {
    localStorage.setItem(THEME_STORAGE_KEY, theme);
  } catch {
    // The choice just will not survive a reload. Nothing else breaks.
  }
}

export function useTheme(): {
  theme: Theme;
  resolved: ResolvedTheme;
  setTheme: (theme: Theme) => void;
} {
  const [theme, setThemeState] = useState<Theme>(readStoredTheme);
  const [systemTheme, setSystemTheme] = useState<ResolvedTheme>(() => resolveTheme('system'));

  // Kept subscribed regardless of the current preference, so that switching
  // back to `system` already knows what the OS is saying.
  useEffect(() => {
    const query = window.matchMedia('(prefers-color-scheme: dark)');
    const onChange = () => setSystemTheme(resolveTheme('system'));
    query.addEventListener('change', onChange);
    return () => query.removeEventListener('change', onChange);
  }, []);

  const resolved: ResolvedTheme = theme === 'system' ? systemTheme : theme;

  // Because `resolved` folds in the OS listener above, a machine that flips at
  // sunset re-themes the register without a reload.
  useEffect(() => {
    applyResolvedTheme(resolved);
  }, [resolved]);

  const setTheme = useCallback((next: Theme) => {
    setThemeState(next);
    writeStoredTheme(next);
  }, []);

  return { theme, resolved, setTheme };
}
